import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, CircularProgress, Alert, IconButton, Tabs, Tab } from '@mui/material';
import { Close } from '@mui/icons-material';
import { emailChannelsAPI } from '../services/api';

interface TemplatePreviewModalProps {
  open: boolean;
  onClose: () => void;
  channelId?: number;
  previewData: {
    template_type: string;
    custom_template_html?: string;
    custom_template_text?: string;
    variables: Record<string, string>;
    response_tone: string;
    signature_position: string;
    include_branding: boolean;
  };
}

const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  open,
  onClose,
  channelId,
  previewData
}) => {
  const [loading, setLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open) {
      loadPreview();
    }
  }, [open, previewData]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      // If no channelId (during creation), use a mock preview
      if (!channelId) {
        // Generate a simple mock preview
        const mockContent = `
          <div style="font-family: Arial, sans-serif; line-height: 1.6; max-width: 600px; margin: 0 auto;">
            ${previewData.include_branding && previewData.variables.company_name ? `
              <div style="background: #f8f9fa; padding: 20px; text-align: center; border-bottom: 3px solid #007bff;">
                <h2 style="margin: 0; color: #333;">${previewData.variables.company_name}</h2>
              </div>
            ` : ''}
            <div style="padding: 20px;">
              <p>Hi [Customer Name],</p>
              <p>Thank you for reaching out to us. This is a sample preview of how your email responses will look.</p>
              <p>Your actual AI-generated content will appear here, formatted according to your selected template type (${previewData.template_type}) and tone (${previewData.response_tone}).</p>
              ${previewData.signature_position === 'top' || previewData.signature_position === 'bottom' ? `
                <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd;">
                  <p style="margin: 5px 0;">Best regards,</p>
                  ${previewData.variables.agent_name ? `<p style="margin: 5px 0; font-weight: bold;">${previewData.variables.agent_name}</p>` : ''}
                  ${previewData.variables.company_name ? `<p style="margin: 5px 0; font-style: italic; color: #666;">${previewData.variables.company_name}</p>` : ''}
                  ${previewData.variables.support_email ? `<p style="margin: 5px 0; color: #666;">${previewData.variables.support_email}</p>` : ''}
                </div>
              ` : ''}
            </div>
          </div>
        `;

        const mockText = `
Hi [Customer Name],

Thank you for reaching out to us. This is a sample preview of how your email responses will look.

Your actual AI-generated content will appear here, formatted according to your selected template type (${previewData.template_type}) and tone (${previewData.response_tone}).

${previewData.signature_position !== 'none' ? `
Best regards,
${previewData.variables.agent_name || '[Agent Name]'}
${previewData.variables.company_name || '[Company Name]'}
${previewData.variables.support_email || ''}
` : ''}
        `.trim();

        setPreviewHtml(mockContent);
        setPreviewText(mockText);
      } else {
        const response = await emailChannelsAPI.previewTemplate(channelId, {
          template_type: previewData.template_type,
          custom_template_html: previewData.custom_template_html,
          custom_template_text: previewData.custom_template_text,
          variables: previewData.variables,
        });
        setPreviewHtml(response.html || '');
        setPreviewText(response.text || '');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Email Template Preview
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && (
          <Box>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
              <Tab label="HTML Preview" />
              <Tab label="Plain Text" />
            </Tabs>

            {activeTab === 0 && previewHtml && (
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#f9f9f9',
                  minHeight: 300,
                }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            )}

            {activeTab === 1 && previewText && (
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#f9f9f9',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  minHeight: 300,
                }}
              >
                {previewText}
              </Box>
            )}
          </Box>
        )}

        {!loading && !error && !previewHtml && !previewText && (
          <Alert severity="info">
            No preview available. Please configure your template settings.
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TemplatePreviewModal;
